import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import { subscriptionRouteConfig } from '../../config/subscriptionConfig';
import { SubscriptionProvider } from '../../contexts/SubscriptionContext';
import InitLoader from '../../components/InitLoader/InitLoader';

function isSubscriptionActive(org) {
  if (!org) return null;
  if (org.sub_force_allow === true) return true;
  if (org.status === 'active') return true;
  if (org.status === 'free_trial') {
    if (!org.trial_end) return false;
    return new Date(org.trial_end) > new Date();
  }
  return false;
}

function SubscriptionGuard({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const userAttributes = useSelector((state) => state.auth.userAttributes);
  const organizationAttributes = useSelector((state) => state.auth.organization);
  const organizationError = useSelector((state) => state.auth.organizationError);

  const userRole = userAttributes?.['custom:role'];
  const isSuperAdmin = userRole === 'super_admin';
  const isOrgAdmin = userRole === 'org_admin';

  const hasSubscription = isSuperAdmin
    ? true
    : isSubscriptionActive(organizationAttributes);

  const subscriptionData = {
    isActive: hasSubscription,
    status: organizationAttributes?.status ?? null,
    trialEnd: organizationAttributes?.trial_end ?? null,
    currentPeriodEnd: organizationAttributes?.current_period_end ?? null,
    planName: organizationAttributes?.plan_name ?? null,
    seatsProviders: organizationAttributes?.seats_providers ?? 0,
    seatsSupport: organizationAttributes?.seats_support ?? 0,
    isSubForceAllowed: organizationAttributes?.sub_force_allow === true,
    userRole,
  };

  useEffect(() => {
    const currentPath = location.pathname;

    if (subscriptionRouteConfig.isSubscriptionRoute(currentPath)) return;
    if (isSuperAdmin) return;
    if (organizationAttributes?.sub_force_allow === true) return;
    if (hasSubscription === null) return;

    if (subscriptionRouteConfig.pathRequiresSubscription(currentPath) && !hasSubscription) {
      const status = organizationAttributes?.status;

      if (status === 'free_trial') {
        if (currentPath !== '/landing') {
          navigate('/landing', { replace: true });
        }
        return;
      }

      if (isOrgAdmin) {
        navigate('/settings/organization/billing', { replace: true });
        return;
      }

      navigate('/subscription-required', { replace: true });
    }
  }, [
    organizationAttributes,
    userRole,
    isSuperAdmin,
    isOrgAdmin,
    hasSubscription,
    location.pathname,
    navigate,
  ]);

  if (organizationError && !organizationAttributes && !isSuperAdmin && hasSubscription !== false) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center p-6 max-w-md">
          <p className="text-muted-foreground mb-4">{organizationError}</p>
          <button onClick={() => window.location.reload()} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (hasSubscription === null && !subscriptionRouteConfig.isSubscriptionRoute(location.pathname)) {
    return <InitLoader message="Checking subscription status..." />;
  }

  return (
    <SubscriptionProvider subscriptionData={subscriptionData}>
      {children}
    </SubscriptionProvider>
  );
}

SubscriptionGuard.propTypes = {
  children: PropTypes.node.isRequired,
};

export default SubscriptionGuard;
